import { z } from 'zod';

/**
 * Directory sync options (APP-113): which Entra accounts the sync brings in, how it connects
 * records MyRewards already has, and whether a first sign-in creates a profile. Pure shapes; the
 * server's planner applies them.
 */

/**
 * The account fields a filter can test: only fields the worker's `User.Read.All` returns. Hire
 * date is left out on purpose (it needs `User-LifeCycleInfo.Read.All`), and so is "account
 * enabled", which is the leaver signal, not a filter (APP-113 §1b).
 */
export const SYNC_FILTER_ATTRIBUTES = [
  'employeeType',
  'department',
  'jobTitle',
  'office',
  'companyName',
  'division',
  'costCenter',
  'employeeId',
  'workEmail',
  'signInName',
  'country',
  'state',
  'city',
  'usageLocation',
  'extensionAttribute1',
  'extensionAttribute2',
  'extensionAttribute3',
  'extensionAttribute4',
  'extensionAttribute5',
  'extensionAttribute6',
  'extensionAttribute7',
  'extensionAttribute8',
  'extensionAttribute9',
  'extensionAttribute10',
  'extensionAttribute11',
  'extensionAttribute12',
  'extensionAttribute13',
  'extensionAttribute14',
  'extensionAttribute15',
  'memberOf',
] as const;
export type SyncFilterAttribute = (typeof SYNC_FILTER_ATTRIBUTES)[number];

export const SYNC_FILTER_OPERATORS = [
  'is',
  'contains',
  'startsWith',
  'endsWith',
  'isEmpty',
] as const;
export type SyncFilterOperator = (typeof SYNC_FILTER_OPERATORS)[number];

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const MAX_FILTER_RULES = 20;
export const MAX_FILTER_VALUES = 50;
export const MAX_FILTER_GROUPS = 10;

export const syncFilterRuleSchema = z
  .object({
    attribute: z.enum(SYNC_FILTER_ATTRIBUTES),
    operator: z.enum(SYNC_FILTER_OPERATORS),
    values: z
      .array(
        z
          .string()
          .trim()
          .min(1)
          .max(256)
          // Accents don't matter when matching, so a value of accents alone would match anything.
          .refine((v) => v.normalize('NFD').replace(/\p{M}/gu, '').trim() !== '', {
            message: 'A value needs a letter, number or symbol, not only accents.',
          }),
      )
      .max(MAX_FILTER_VALUES),
  })
  .strict()
  .superRefine((rule, ctx) => {
    // The messages are what a System Admin reads when a rule can't be saved.
    if (rule.operator === 'isEmpty' ? rule.values.length > 0 : rule.values.length === 0)
      ctx.addIssue({
        code: 'custom',
        path: ['values'],
        message:
          rule.operator === 'isEmpty'
            ? 'A rule that tests for empty takes no values.'
            : 'Give every rule at least one value, or remove it.',
      });
    if (rule.attribute === 'memberOf') {
      if (rule.operator !== 'is')
        ctx.addIssue({
          code: 'custom',
          path: ['operator'],
          message: 'A group rule can only test membership.',
        });
      if (rule.values.some((v) => !GUID.test(v)))
        ctx.addIssue({
          code: 'custom',
          path: ['values'],
          message: 'A group is named by its object ID from the Entra admin center.',
        });
    }
  })
  .transform((rule) =>
    rule.attribute === 'memberOf'
      ? { ...rule, values: rule.values.map((v) => v.toLowerCase()) }
      : rule,
  );
export type SyncFilterRule = z.infer<typeof syncFilterRuleSchema>;

export const syncFiltersSchema = z
  .object({
    /** Bring in guest accounts too (off by default). */
    guests: z.boolean(),
    /**
     * Bring in accounts turned off in Entra that have no record here (APP-131 §3). Off by default,
     * so a first sync of a whole tenant doesn't bring in every past leaver; a value saved before
     * the switch existed reads as off.
     */
    disabledAccounts: z.boolean().default(false),
    /** Only accounts where every one of these is true. */
    include: z.array(syncFilterRuleSchema).max(MAX_FILTER_RULES),
    /** Leave out any account where one of these is true. */
    exclude: z.array(syncFilterRuleSchema).max(MAX_FILTER_RULES),
  })
  .strict()
  .refine((f) => filterGroupIds(f).length <= MAX_FILTER_GROUPS, {
    path: ['include'],
    message: `The filters can name at most ${String(MAX_FILTER_GROUPS)} groups.`,
  });
export type SyncFilters = z.infer<typeof syncFiltersSchema>;

export const DEFAULT_SYNC_FILTERS: SyncFilters = {
  guests: false,
  disabledAccounts: false,
  include: [],
  exclude: [],
};

/** Every group a filter names, once, lower case: the groups the sync reads members of. */
export function filterGroupIds(filters: {
  include: readonly SyncFilterRule[];
  exclude: readonly SyncFilterRule[];
}): string[] {
  const ids = new Set<string>();
  for (const rule of [...filters.include, ...filters.exclude])
    if (rule.attribute === 'memberOf') for (const v of rule.values) ids.add(v.toLowerCase());
  return [...ids];
}

/** What each attribute is called on screen and in the audit log. */
export const SYNC_FILTER_ATTRIBUTE_WORDS: Readonly<Record<SyncFilterAttribute, string>> = {
  employeeType: 'Employee type',
  department: 'Department',
  jobTitle: 'Job title',
  office: 'Office',
  companyName: 'Company name',
  division: 'Division',
  costCenter: 'Cost center',
  employeeId: 'Employee ID',
  workEmail: 'Work email',
  signInName: 'Sign-in name',
  country: 'Country or region',
  state: 'State',
  city: 'City',
  usageLocation: 'Usage location',
  extensionAttribute1: 'Extension attribute 1',
  extensionAttribute2: 'Extension attribute 2',
  extensionAttribute3: 'Extension attribute 3',
  extensionAttribute4: 'Extension attribute 4',
  extensionAttribute5: 'Extension attribute 5',
  extensionAttribute6: 'Extension attribute 6',
  extensionAttribute7: 'Extension attribute 7',
  extensionAttribute8: 'Extension attribute 8',
  extensionAttribute9: 'Extension attribute 9',
  extensionAttribute10: 'Extension attribute 10',
  extensionAttribute11: 'Extension attribute 11',
  extensionAttribute12: 'Extension attribute 12',
  extensionAttribute13: 'Extension attribute 13',
  extensionAttribute14: 'Extension attribute 14',
  extensionAttribute15: 'Extension attribute 15',
  memberOf: 'Member of group',
};

export const SYNC_FILTER_OPERATOR_WORDS: Readonly<Record<SyncFilterOperator, string>> = {
  is: 'is',
  contains: 'contains',
  startsWith: 'starts with',
  endsWith: 'ends with',
  isEmpty: 'is empty',
};

/**
 * A rule in words: "Department is Board or Advisors", "Member of group Finance". A group is called
 * by its name in Entra when a read has learned it (APP-131 §4), else by its object ID's start.
 */
export function filterRuleLabel(
  rule: SyncFilterRule,
  groupNames: Readonly<Record<string, string>> = {},
): string {
  if (rule.attribute === 'memberOf')
    return `Member of group ${rule.values.map((v) => groupNames[v.toLowerCase()] ?? `${v.slice(0, 8)}…`).join(' or ')}`;
  const head = `${SYNC_FILTER_ATTRIBUTE_WORDS[rule.attribute]} ${SYNC_FILTER_OPERATOR_WORDS[rule.operator]}`;
  return rule.operator === 'isEmpty' ? head : `${head} ${rule.values.join(' or ')}`;
}

/** The fields a matching rule compares (APP-113 §6). */
export const MATCH_KEYS = ['eeNumber', 'signInName', 'workEmail', 'name'] as const;
export type MatchKey = (typeof MATCH_KEYS)[number];

export const MATCH_KEY_WORDS: Readonly<Record<MatchKey, string>> = {
  eeNumber: 'EE#',
  signInName: 'Sign-in name',
  workEmail: 'Work email',
  name: 'First and last name',
};

export const matchRuleSchema = z
  .object({
    key: z.enum(MATCH_KEYS),
    /** A different last name turns a match into a question. Ignored for the name key. */
    alsoLastName: z.boolean(),
    /** Connect automatically, or ask an Admin. */
    action: z.enum(['connect', 'ask']),
  })
  .strict()
  // Two people can share a name, and only one may be in scope: a name match always asks.
  .refine((r) => !(r.key === 'name' && r.action === 'connect'), { path: ['action'] });
export type MatchRule = z.infer<typeof matchRuleSchema>;

export const matchingSchema = z
  .object({ rules: z.array(matchRuleSchema).max(MATCH_KEYS.length) })
  .strict()
  .refine((m) => new Set(m.rules.map((r) => r.key)).size === m.rules.length, {
    path: ['rules'],
  });
export type MatchingSetting = z.infer<typeof matchingSchema>;

export const DEFAULT_MATCH_RULES: readonly MatchRule[] = [
  { key: 'eeNumber', alsoLastName: true, action: 'connect' },
  { key: 'signInName', alsoLastName: true, action: 'connect' },
  { key: 'workEmail', alsoLastName: true, action: 'connect' },
  { key: 'name', alsoLastName: false, action: 'ask' },
];

/**
 * Profiles at first sign-in (APP-113 §4d, §12h): while the sync is switched off, and, only when
 * `beforeFirstSync` is on, while it's on but hasn't finished a run. A new installation stays
 * closed to all but its listed first administrators until its first full sync (APP-096).
 */
export const firstSignInSchema = z
  .object({ createProfile: z.boolean(), beforeFirstSync: z.boolean().default(false) })
  .strict();
export type FirstSignInSetting = z.infer<typeof firstSignInSchema>;
